import React, { useState } from "react";
import { PiWaveform, PiTextAlignLeft, PiVideoCamera } from "react-icons/pi";

const accents = ["#3E8DFF", "#FF8F47", "#3EB567", "#FF7AAC"];

const KindIcon = ({ kind }) => {
  if (kind === "video") return <PiVideoCamera className="h-3.5 w-3.5 text-white" aria-hidden="true" />;
  if (kind === "audio") return <PiWaveform className="h-3.5 w-3.5 text-white" aria-hidden="true" />;
  return <PiTextAlignLeft className="h-3.5 w-3.5 text-white" aria-hidden="true" />;
};

const SelectProofRow = ({ initProof, index, initSelected, addNewSelection, removeNewSelection, incompatible = false }) => {
  const accent = accents[index % accents.length];
  const [proof] = useState(initProof);

  // Block *adding* incompatible proofs, but allow *removing* them — if a
  // user picked proofs and then changed widget kind, they need a way to
  // clear the now-invalid selection.
  const blockClick = incompatible && !initSelected;

  const handleSelect = () => {
    if (initSelected) {
      removeNewSelection(initProof);
    } else {
      addNewSelection(initProof);
    }
  };

  return (
    <button
      type="button"
      onClick={handleSelect}
      disabled={blockClick}
      title={incompatible ? "This widget can't render this proof type." : undefined}
      className={`relative w-full text-left bg-white border-[3px] border-black p-3 shadow-[3px_3px_0_rgba(0,0,0,0.15)] transition-all ${
        blockClick
          ? "opacity-50 cursor-not-allowed"
          : `hover:shadow-[4px_4px_0_rgba(0,0,0,0.22)] hover:-translate-y-0.5 ${incompatible ? "opacity-60" : ""}`
      }`}
    >
      {incompatible && (
        <span className="absolute top-1.5 right-1.5 text-[9px] uppercase tracking-wider font-black bg-red-500 text-white px-1.5 py-0.5 border border-black">
          incompatible
        </span>
      )}
      <div className="flex items-start gap-3 min-w-0">
        {proof.avatar && (
          <div className="relative flex-none hidden sm:block">
            <img
              className="w-14 h-14 object-cover border-[2px]"
              style={{ borderColor: accent }}
              src={proof.avatar}
              alt={proof.name}
            />
            <span
              className="absolute -bottom-1 -right-1 px-1 py-0.5 flex items-center justify-center"
              style={{ background: accent }}
            >
              <KindIcon kind={proof.kind} />
            </span>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="zz-display text-base sm:text-lg font-black leading-tight truncate">{proof.headline}</div>
          <div className="mt-1 flex gap-x-1.5 items-center text-xs text-slate-600 truncate">
            <span className="font-bold">{proof.name}</span>
            {proof.contact_title && <span>· {proof.contact_title}</span>}
            {proof.contact_company_name && <span>@ {proof.contact_company_name}</span>}
          </div>
          <div className="mt-1 flex gap-x-2 items-center text-[10px] uppercase tracking-wider font-bold text-slate-500">
            <time dateTime={proof.given_at_utc}>{proof.given_at_utc}</time>
            {proof.source_platform && <span>·</span>}
            {proof.source_platform && <span className="capitalize">{proof.source_platform}</span>}
          </div>
        </div>
        <div
          className={`flex-none w-7 h-7 flex items-center justify-center border-[2px] border-black text-base font-black ${initSelected ? "text-white" : "text-slate-300 bg-white"}`}
          style={initSelected ? { background: accent } : undefined}
          aria-hidden="true"
        >
          {initSelected ? "✓" : "+"}
        </div>
      </div>
      {proof.snippet && (
        <div
          className="prose-sm mt-2 text-xs sm:text-sm text-slate-700 line-clamp-2"
          dangerouslySetInnerHTML={{ __html: proof.snippet }}
        />
      )}
    </button>
  );
};

export default SelectProofRow;
